import React from "react";
import {
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import {
    CheckCircle as CheckIcon,
    ExpandMore as ExpandMoreIcon,
    PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import AdSense from "../../components/AdSense";
import SEO from "../../components/SEO/SEO";
import PageHero from "../../components/PageHero/PageHero";
import AppDownloadCTA, {
    PageShell,
} from "../../components/AppDownloadCTA/AppDownloadCTA";
import { PhoneMockup } from "../../components/ScreenshotGallery/ScreenshotGallery";
import { pageMeta } from "../../seo/pageMeta";
import { APP_SCREENSHOTS } from "../../seo/assets";
import { SITE_URL } from "../../seo/constants";

const ARABIC_PDF =
    "https://firebasestorage.googleapis.com/v0/b/quran-chat-buddy-prayer-time.firebasestorage.app/o/SurahYaseen%2Fsurah_yaseen%20arabic.pdf?alt=media&token=b73f89db-49c3-4ae8-be37-b3ef90130be6";
const ENGLISH_PDF =
    "https://firebasestorage.googleapis.com/v0/b/quran-chat-buddy-prayer-time.firebasestorage.app/o/SurahYaseen%2FSurah-Yaseen-Transliteration-PDF-01.pdf?alt=media&token=64a0f6f9-4b81-4788-9b88-cf8d89d33949";

const faqs = [
    {
        q: "How many verses are in Surah Yaseen?",
        a: "Surah Yaseen (Surah Yasin) contains 83 verses and is the 36th chapter of the Holy Quran.",
    },
    {
        q: "Can I download Surah Yaseen PDF free?",
        a: "Yes. Use the Arabic and English download buttons above for free offline Surah Yaseen PDF files.",
    },
    {
        q: "Can I read Surah Yasin with English translation?",
        a: "Yes. Download the English PDF version to read Surah Yaseen with translation and understand its meaning.",
    },
    {
        q: "Why is Surah Yaseen important for Muslims?",
        a: "Surah Yaseen teaches faith, prophethood, resurrection, and Allah’s mercy. Many Muslims recite it regularly for reflection and spiritual comfort.",
    },
];

export default function SurahYaseen() {
    const meta = pageMeta.surahYaseen;

    return (
        <PageShell maxWidth="md">
            <SEO
                {...meta}
                image={`${SITE_URL}/images/screenshots/screen-3.webp`}
                keywords="Surah Yaseen, Surah Yasin, Surah Yaseen PDF, Surah Yasin PDF, Heart of the Quran, Quran PDF, Islamic app, Muslim app, Quran Chat Buddy"
                jsonLd={[
                    {
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        name: meta.title,
                        description: meta.description,
                        url: `${SITE_URL}${meta.path}`,
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        mainEntity: faqs.map((f) => ({
                            "@type": "Question",
                            name: f.q,
                            acceptedAnswer: {
                                "@type": "Answer",
                                text: f.a,
                            },
                        })),
                    },
                ]}
            />

            <PageHero
                eyebrow="Surah Yasin · Chapter 36"
                title="Surah Yaseen PDF Download (Arabic & English)"
                subtitle="Download Surah Yaseen PDF free — often called the Heart of the Quran. Read Surah Yasin offline in Arabic or with English translation for daily recitation, memorization, and reflection."
                breadcrumbs={[
                    { label: "Home", to: "/" },
                    { label: "Surah Yaseen PDF" },
                ]}
            />

            <Grid container spacing={3} sx={{ mb: 4 }} alignItems="center">
                <Grid item xs={12} md={7}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Card sx={{ height: "100%", textAlign: "center" }}>
                                <CardContent sx={{ p: 3 }}>
                                    <PdfIcon
                                        sx={{ fontSize: 40, color: "primary.main", mb: 1 }}
                                    />
                                    <Typography variant="h5" component="h2" sx={{ mb: 1.5 }}>
                                        Arabic Surah Yaseen
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ mb: 2.5, color: "text.secondary" }}
                                    >
                                        Arabic Surah Yaseen PDF for daily tilawah,
                                        memorization, and offline reading.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        href={ARABIC_PDF}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        fullWidth
                                    >
                                        Download Arabic PDF
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Card sx={{ height: "100%", textAlign: "center" }}>
                                <CardContent sx={{ p: 3 }}>
                                    <PdfIcon
                                        sx={{ fontSize: 40, color: "secondary.main", mb: 1 }}
                                    />
                                    <Typography variant="h5" component="h2" sx={{ mb: 1.5 }}>
                                        English Translation
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ mb: 2.5, color: "text.secondary" }}
                                    >
                                        Surah Yaseen with English meaning — understand
                                        the message of faith, mercy, and the Hereafter.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        href={ENGLISH_PDF}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        fullWidth
                                    >
                                        Download English PDF
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} md={5}>
                    <PhoneMockup
                        src={APP_SCREENSHOTS[2].src}
                        alt="Daily ayat reflections in Quran Chat Buddy"
                        maxWidth={220}
                    />
                </Grid>
            </Grid>

            <Box sx={{ my: 4 }}>
                <AdSense slot="2566597353" />
            </Box>

            <Typography variant="h4" component="h2" sx={{ mb: 2, fontWeight: 700 }}>
                What is Surah Yaseen?
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                Surah Yaseen (also spelled Surah Yasin) is the 36th chapter of the Holy
                Quran and contains 83 verses. It is one of the most recited surahs among
                Muslims worldwide and is often referred to as the Heart of the Quran.
            </Typography>
            <List dense>
                {[
                    "Surah Number: 36",
                    "Total Verses: 83",
                    "Revelation: Makki",
                    "Juz: 22 and 23",
                    "Themes: Faith, Resurrection, and Divine Mercy",
                ].map((item) => (
                    <ListItem key={item} sx={{ py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                    </ListItem>
                ))}
            </List>

            <Typography variant="h4" component="h2" sx={{ mt: 4, mb: 2, fontWeight: 700 }}>
                Benefits of Reading Surah Yaseen
            </Typography>
            <List>
                {[
                    "Strengthens faith and trust in Allah",
                    "Encourages reflection on the Quran",
                    "Reminds believers about the Hereafter",
                    "Provides spiritual comfort and guidance",
                    "Helps build a consistent Quran reading habit",
                ].map((item) => (
                    <ListItem key={item} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                    </ListItem>
                ))}
            </List>

            <Typography variant="body1" sx={{ mt: 2, mb: 3, lineHeight: 1.8 }}>
                Looking for the full mushaf? Visit our{" "}
                <Box
                    component={Link}
                    to="/quran-pdf-download"
                    sx={{ color: "primary.main" }}
                >
                    free Quran PDF download
                </Box>{" "}
                page, or learn more in our{" "}
                <Box component={Link} to="/blogs/quran" sx={{ color: "primary.main" }}>
                    Holy Quran guide
                </Box>
                .
            </Typography>

            <Typography variant="h4" component="h2" sx={{ mb: 2, fontWeight: 700 }}>
                Frequently Asked Questions
            </Typography>
            {faqs.map((f) => (
                <Accordion
                    key={f.q}
                    disableGutters
                    elevation={0}
                    sx={{
                        mb: 1,
                        border: "1px solid rgba(46,139,87,0.12)",
                        borderRadius: "12px !important",
                        "&:before": { display: "none" },
                    }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography fontWeight={600}>{f.q}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                            {f.a}
                        </Typography>
                    </AccordionDetails>
                </Accordion>
            ))}

            <AppDownloadCTA
                title="Recite, learn & grow with Quran Chat Buddy"
                subtitle="Continue beyond Surah Yaseen with AI Quran chat, Salah prayer times, Qibla, daily ayat, and Islamic learning — free on iOS and Android."
            />
        </PageShell>
    );
}
